// Product management types

export enum ProductStatus {
  DRAFT = 'DRAFT',
  ACTIVE = 'ACTIVE',
  ARCHIVED = 'ARCHIVED',
  DISCONTINUED = 'DISCONTINUED',
  OUT_OF_STOCK = 'OUT_OF_STOCK',
}

export enum GSTSlab {
  ZERO = '0%',
  FIVE = '5%',
  TWELVE = '12%',
  EIGHTEEN = '18%',
  TWENTY_EIGHT = '28%',
}

export enum ProductUnit {
  PCS = 'pcs',
  PACK_OF = 'pack of',
  SET_OF = 'set of',
  BOX_OF = 'box of',
}

export enum DiscountType {
  PERCENTAGE = 'percentage',
  FIXED = 'fixed',
}

export interface DiscountInfo {
  type: DiscountType
  amount: number
}

export interface MarketplaceLinks {
  amazon?: string
  flipkart?: string
  meesho?: string
}

export interface PDFAttachment {
  id: string
  filename: string
  label: string
  url: string
  size: number
  upload_date: string
}

export interface StockByWarehouse {
  warehouse_id: string
  warehouse_name: string
  stock: number
}

export interface Category {
  id: string
  name: string
  slug: string
  description?: string
  parent_id?: string
  sort_order: number
  is_active: boolean
  meta_title?: string
  meta_description?: string
  meta_keywords?: string
  created_at: string
  updated_at: string
  product_count?: number
  children?: Category[]
}

export interface CategoryCreate {
  name: string
  slug: string
  description?: string
  parent_id?: string
  sort_order?: number
  is_active?: boolean
  meta_title?: string
  meta_description?: string
  meta_keywords?: string
}

export interface CategoryUpdate extends Partial<CategoryCreate> {}

export interface Variant {
  id: string
  name: string
  item_code: string
  sku: string
  product_id: string
  attributes: Record<string, any>
  mrp?: number
  sale_price?: number
  wholesale_price?: number
  purchase_price?: number
  weight?: number
  dimensions: Record<string, any>
  is_active: boolean
  is_online_enabled: boolean
  image?: string
  images: string[]
  // NO inventory fields - stock is managed at warehouse level via ProductStock table
  created_at: string
  updated_at: string
}

export interface VariantCreate {
  name: string
  item_code: string
  sku: string
  product_id: string
  attributes?: Record<string, any>
  mrp?: number
  sale_price?: number
  wholesale_price?: number
  purchase_price?: number
  weight?: number
  dimensions?: Record<string, any>
  is_active?: boolean
  is_online_enabled?: boolean
  image?: string
  images?: string[]
  // NO inventory fields - stock is managed at warehouse level via ProductStock table
}

export interface VariantUpdate extends Partial<Omit<VariantCreate, 'product_id'>> {}

export interface Product {
  id: string
  name: string
  item_code: string
  sku: string
  hsn_code: string
  description?: string
  category_id: string
  brand_id?: string
  brand?: {
    id: string
    name: string
    code: string
    logo_url?: string
  }
  
  // B2B Package Information
  moq: number
  inner_box_quantity?: number
  carton_quantity?: number
  carton_rate?: number
  product_unit: ProductUnit
  set_size: number
  is_set_selling: boolean
  
  // Pricing
  mrp: number
  sale_price: number
  wholesale_price: number
  purchase_price: number
  
  // GST
  gst_rate: number
  gst_slab?: GSTSlab // Keep for backward compatibility
  
  // NO inventory fields at product level - stock is managed at warehouse level via ProductStock table
  
  // Status
  status: ProductStatus
  is_active: boolean
  is_online_enabled: boolean
  
  // Physical attributes
  weight?: number
  dimensions: Record<string, any>
  
  // Media
  image?: string
  images: string[]
  
  // SEO and metadata
  seo_data: Record<string, any>
  schema_org_data: Record<string, any>
  product_metadata: Record<string, any>
  
  // Timestamps
  created_at: string
  updated_at: string
  
  // Relations
  category?: Category
  variants: Variant[]
  
  // Calculated fields
  discounted_prices?: Record<string, number>
  
  // Metadata fields (extracted from product_metadata)
  marketplace_links?: MarketplaceLinks
  discount?: DiscountInfo
  pdf_attachments?: PDFAttachment[]
  youtube_videos?: string[]
  
  // Stock information (read-only, aggregated from ProductStock table)
  stock_by_warehouse?: Array<{
    warehouse_id: string
    warehouse_name: string
    quantity: number
    reserved: number
    available: number
  }>
  total_stock?: number
  has_variants?: boolean
  stock_by_variant?: Record<string, {
    variant_name: string
    variant_sku: string
    total_stock: number
    warehouses: Array<{
      warehouse_id: string
      warehouse_name: string
      quantity: number
      reserved: number
      available: number
    }>
  }>
}

export interface ProductCreate {
  name: string
  item_code?: string  // Optional - auto-generated if not provided
  sku?: string  // Optional - auto-generated if not provided
  hsn_code: string
  description?: string
  category_id: string
  brand_id?: string
  
  // B2B Package Information
  moq?: number
  inner_box_quantity?: number
  carton_quantity?: number
  carton_rate?: number
  product_unit?: ProductUnit
  set_size?: number
  is_set_selling?: boolean
  
  // Pricing
  mrp?: number
  sale_price?: number
  wholesale_price?: number
  purchase_price?: number
  
  // GST
  gst_rate?: number
  gst_slab?: GSTSlab // Keep for backward compatibility
  
  // NO inventory fields - stock is managed at warehouse level
  
  // Status
  status?: ProductStatus
  is_active?: boolean
  is_online_enabled?: boolean
  
  // Physical attributes
  weight?: number
  dimensions?: Record<string, any>
  
  // Media
  image?: string
  images?: string[]
  
  // SEO and metadata
  seo_data?: Record<string, any>
  schema_org_data?: Record<string, any>
  product_metadata?: Record<string, any>
  
  // Optional convenience fields (will be stored in product_metadata)
  marketplace_links?: MarketplaceLinks
  discount?: DiscountInfo
}

export interface ProductUpdate extends Partial<ProductCreate> {}

export interface BulkImportResult {
  total_rows: number
  successful_imports: number
  failed_imports: number
  errors: Array<{
    row: number
    field?: string
    message: string
  }>
}

export interface PaginatedResponse<T> {
  items: T[]
  total: number
  page: number
  per_page: number
  pages: number
}
